import { initFirebase } from '../firebase'
import { collection, getDocs, query, orderBy, limit, where, doc, getDoc, onSnapshot } from 'firebase/firestore'

const { db } = initFirebase()

// Astrocartography ("Five Places") — the fifth paid Store report
// (reportType == 'astrocartography'). Synchronous like Celebrity Twins: phase 0
// writes the five places, the directions and the full line map in one go (no
// model), then the five place sections run in parallel and the directions
// section closes after them.
//   reportStats/astrocartography — write-time rollup (count / completed /
//     partial / failed / repairs / totalCostUSD / totalMs).
//   report_generations (reportType == 'astrocartography') — one row per run:
//     per-slice cost (place1..place5, directions) + places[] {id, score, bucket}.
//   reports (reportType == 'astrocartography') — the report: places, lines,
//     directions, per-section prose. Watched live for the in-flight view.
//   cities — the city pool (active / paused / draft). Feeds the Cities page.
//   system_logs (functionName == 'generateAstrocartographyReport') — error/warn.
//
// Same access path as the other report dashboards; single-field where() only
// (auto-indexed), sorted/limited client-side.

export const MAP_SLICES = ['place1', 'place2', 'place3', 'place4', 'place5', 'directions']
export const PLACE_SLICES = ['place1', 'place2', 'place3', 'place4', 'place5']

const ERROR_FN = 'generateAstrocartographyReport'

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

function toMs(v) {
  if (!v) return 0
  if (typeof v === 'object' && typeof v.toMillis === 'function') return v.toMillis()
  if (typeof v === 'object' && (v.seconds || v._seconds)) return (v.seconds || v._seconds) * 1000
  const t = new Date(v).getTime()
  return isNaN(t) ? 0 : t
}

const arr = (v) => Array.isArray(v) ? v : []
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v)) ? v : {}

function normStats(x) {
  return {
    count: num(x.count),
    completed: num(x.completed),
    partial: num(x.partial),
    failed: num(x.failed),
    repairs: num(x.repairs),
    totalCostUSD: num(x.totalCostUSD),
    totalMs: num(x.totalMs),
    totalInputTokens: num(x.totalInputTokens),
    totalOutputTokens: num(x.totalOutputTokens),
    lastAtMs: toMs(x.lastAt)
  }
}

function mapLine(l) {
  return {
    planet: l.planet || '',
    angle: l.angle || '',
    km: num(l.km),
    label: l.label || `${l.planet || '?'}–${l.angle || '?'}`,
    tone: l.tone || '',
    bucket: l.bucket || ''
  }
}

function mapPlace(p) {
  return {
    rank: num(p.rank),
    cityId: p.cityId || '',
    city: p.city || '',
    country: p.country || '',
    countryCode: p.countryCode || '',
    flag: p.flag || '',
    photo: (p.photo && typeof p.photo === 'object') ? p.photo : null,
    latitude: num(p.latitude),
    longitude: num(p.longitude),
    distanceFromBirthKm: num(p.distanceFromBirthKm),
    score: num(p.score),
    bucket: p.bucket || '',
    lines: arr(p.lines).map(mapLine),
    cautions: arr(p.cautions).map(mapLine)
  }
}

function mapReport(d) {
  const x = d.data()
  const g = obj(x.generation)
  const readyAt = obj(g.sectionsReadyAt)
  const sectionsReadyAtMs = {}
  Object.keys(readyAt).forEach(k => { sectionsReadyAtMs[k] = toMs(readyAt[k]) })
  const u = obj(x.user)
  const ub = obj(u.bigThree)
  const birth = obj(x.birth)
  const lines = obj(x.lines)
  return {
    id: d.id,
    reportId: x.reportId || d.id,
    userId: x.userId || '',
    language: x.language || '',
    title: x.title || '',
    timeAccuracy: x.timeAccuracy || u.timeAccuracy || '',
    poolSize: num(x.poolSize),
    user: {
      name: u.name || '',
      birthDate: u.birthDate || '',
      birthPlace: u.birthPlace || '',
      sun: ub.sun || '',
      moon: ub.moon || '',
      rising: ub.rising || '',
      dominant: u.dominant || ''
    },
    birth: {
      latitude: birth.latitude == null ? null : num(birth.latitude),
      longitude: birth.longitude == null ? null : num(birth.longitude),
      countryCode: birth.countryCode || ''
    },
    places: arr(x.places).map(mapPlace).sort((a, b) => a.rank - b.rank),
    directions: obj(x.directions),
    lines: {
      gst: num(lines.gst),
      obliquity: num(lines.obliquity),
      orbKm: num(lines.orbKm),
      birthplaceExclusionKm: num(lines.birthplaceExclusionKm),
      curves: arr(lines.curves).map(c => ({ planet: c.planet || '', angle: c.angle || '', points: arr(c.points) }))
    },
    // Raw per-section prose (place1..place5 objects, directions array).
    content: obj(x.sections),
    state: g.state || '',
    sections: obj(g.sections),
    sectionsReadyAtMs,
    promptVersion: g.promptVersion || '',
    purchase: x.purchase || null,
    createdAtMs: toMs(x.createdAt) || toMs(x.purchasedAt),
    purchasedAtMs: toMs(x.purchasedAt),
    startedAtMs: toMs(g.startedAt),
    finishedAtMs: toMs(g.finishedAt)
  }
}

function mapGen(d) {
  const x = d.data()
  return {
    id: d.id,
    reportId: x.reportId || '',
    userId: x.userId || '',
    language: x.language || '',
    promptVersion: x.promptVersion || '',
    state: x.state || '',
    timeAccuracy: x.timeAccuracy || '',
    partialRepair: x.partialRepair === true,
    poolSize: num(x.poolSize),
    totalMs: num(x.totalMs),
    inputTokens: num(x.inputTokens),
    outputTokens: num(x.outputTokens),
    costUsd: num(x.costUsd),
    slices: obj(x.slices),
    slicesFailed: arr(x.slicesFailed),
    places: arr(x.places).map(p => ({ id: p.id || '', score: num(p.score), bucket: p.bucket || '' })),
    createdAtMs: toMs(x.createdAt) || toMs(x.timestamp)
  }
}

function mapCity(d) {
  const x = d.data()
  return {
    id: d.id,
    name: x.name || '',
    country: x.country || '',
    countryCode: x.countryCode || '',
    flag: x.flag || '',
    qid: x.qid || '',
    label: x.label || '',
    description: x.description || '',
    latitude: x.latitude == null ? null : num(x.latitude),
    longitude: x.longitude == null ? null : num(x.longitude),
    population: num(x.population),
    photo: (x.photo && typeof x.photo === 'object') ? x.photo : null,
    status: x.status || '',
    pausedReason: x.pausedReason || '',
    pipelineVersion: x.pipelineVersion || '',
    createdAtMs: toMs(x.createdAt),
    updatedAtMs: toMs(x.updatedAt),
    activatedAtMs: toMs(x.activatedAt)
  }
}

export class FirebaseAstrocartographyReportService {
  async getStats() {
    const snap = await getDoc(doc(db, 'reportStats', 'astrocartography'))
    return snap.exists() ? normStats(snap.data()) : null
  }

  async getGenerations(max = 500) {
    const snap = await getDocs(query(collection(db, 'report_generations'), where('reportType', '==', 'astrocartography')))
    return snap.docs
      .map(mapGen)
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
      .slice(0, max)
  }

  // Real-time watch of all astrocartography reports. Returns the unsubscribe fn.
  watchReports(onData, onError) {
    const q = query(collection(db, 'reports'), where('reportType', '==', 'astrocartography'))
    return onSnapshot(
      q,
      snap => onData(snap.docs.map(mapReport).sort((a, b) => b.createdAtMs - a.createdAtMs)),
      err => { if (onError) onError(err) }
    )
  }

  // The full city pool (active + paused + draft). Sorted by name.
  async getCities(max = 2000) {
    const snap = await getDocs(collection(db, 'cities'))
    return snap.docs
      .map(mapCity)
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, max)
  }

  async getErrorLogs(max = 300) {
    const snap = await getDocs(query(collection(db, 'system_logs'), orderBy('createdAt', 'desc'), limit(max)))
    const cutoff = Date.now() - 7 * 86400000
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(x => x.functionName === ERROR_FN && (x.level === 'error' || x.level === 'warn') && toMs(x.createdAt) >= cutoff)
      .map(x => ({
        id: x.id,
        level: x.level,
        message: x.message || '',
        error: x.error || '',
        reportId: (x.metadata && x.metadata.reportId) || '',
        failedSlices: (x.metadata && x.metadata.failedSlices) || null,
        createdAtMs: toMs(x.createdAt)
      }))
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
  }
}
